import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPlanStep,
  CampaignStepRecipient,
} from "@shared-core/data/api-types";
import {
  DISPATCH_METHOD_LABELS,
  mailingTakesTrackingNumbers,
  postedPackStatus,
  trackingNumberPlaceholder,
} from "@shared-core/domain/direct-mail-campaigns";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import { failureLogFields, readCampaignActionError } from "./actionErrors";

/**
 * One recipient's pack on a posted mailing: how it went and, once a
 * courier tracking number is recorded, what the courier last said (CIS
 * reads DPD and DX every two hours in the working day). A DPD, DX or "Some
 * of each" mailing takes a tracking number per pack; any pack can be marked
 * returned, which flags the person's address so later mailings leave them
 * out until it is fixed, and a return marked by mistake can be undone. The server keeps the rules (which courier a number
 * belongs to, and that it matches how the mailing went) and says why it
 * refuses one.
 */
export function PostedPackCell({
  campaignId,
  step,
  recipient,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  recipient: CampaignStepRecipient;
  onChanged: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const saved = recipient.tracking_number ?? "";
  const [number, setNumber] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [confirmingReturn, setConfirmingReturn] = useState(false);
  const [returning, setReturning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = {
    campaign_id: campaignId,
    step_id: step.id,
    person_id: recipient.person_id,
  };
  const inputId = `tracking-${step.id}-${recipient.person_id}`;
  const errorId = `${inputId}-error`;
  const status = postedPackStatus(recipient);
  const isReturned = Boolean(recipient.returned_at);
  const takesNumber =
    mailingTakesTrackingNumbers(step.dispatch_method) && !isReturned;

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const touch = await dataAdapter.setCampaignMailingTrackingNumber(
        campaignId,
        step.id,
        recipient.person_id,
        number,
      );
      setNumber(touch.tracking_number ?? "");
      onChanged();
    } catch (e) {
      console.error(
        "marketing.campaign.tracking_number.failed",
        failureLogFields(e, ids),
      );
      const refusal = readCampaignActionError(
        e,
        "Couldn't save the tracking number. Please try again.",
      );
      setError(refusal.fields.tracking_number ?? refusal.summary);
    } finally {
      setSaving(false);
    }
  }

  async function handleReturned() {
    setReturning(true);
    setError(null);
    try {
      if (isReturned) {
        await dataAdapter.undoCampaignMailingReturned(
          campaignId,
          step.id,
          recipient.person_id,
        );
      } else {
        await dataAdapter.markCampaignMailingReturned(
          campaignId,
          step.id,
          recipient.person_id,
        );
      }
      setConfirmingReturn(false);
      onChanged();
    } catch (e) {
      console.error(
        isReturned
          ? "marketing.campaign.recipient_return_undo.failed"
          : "marketing.campaign.recipient_returned.failed",
        failureLogFields(e, ids),
      );
      setError(
        readCampaignActionError(
          e,
          isReturned
            ? "Couldn't undo the return. Please try again."
            : "Couldn't mark the pack returned. Please try again.",
        ).summary,
      );
    } finally {
      setReturning(false);
    }
  }

  return (
    <div className="space-y-1.5 min-w-56">
      {recipient.dispatch_method && (
        <div className="text-xs text-muted-foreground">
          Posted {formatShortDateOrDash(recipient.sent_on)} by{" "}
          {DISPATCH_METHOD_LABELS[recipient.dispatch_method]}
        </div>
      )}
      {status && (
        <div
          className={
            isReturned
              ? "text-sm font-medium text-amber-800 dark:text-amber-300"
              : "text-sm font-medium text-foreground"
          }
        >
          {status}
          {isReturned && recipient.returned_at && (
            <span className="font-normal text-muted-foreground">
              {" "}
              ({formatShortDateOrDash(recipient.returned_at)})
            </span>
          )}
        </div>
      )}
      {takesNumber && (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <Label htmlFor={inputId} className="sr-only">
            Tracking number for {recipient.name}
          </Label>
          <Input
            id={inputId}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder={trackingNumberPlaceholder(step.dispatch_method)}
            maxLength={35}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="h-8 w-52 text-sm"
          />
          {number.trim() !== saved && (
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? "Saving…" : number.trim() ? "Save" : "Clear"}
            </Button>
          )}
        </form>
      )}
      {confirmingReturn ? (
        <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-2 space-y-2 text-sm">
          <p className="text-amber-900 dark:text-amber-200">
            {isReturned
              ? `Undo the return? ${recipient.name}'s pack counts as posted again, and their address is no longer flagged (unless it has been edited or another pack has come back since).`
              : `Mark ${recipient.name}'s pack returned? Their address is flagged as bad, and later mailings leave them out until it is fixed.`}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleReturned} disabled={returning}>
              {returning
                ? "Saving…"
                : isReturned
                  ? "Yes, undo returned"
                  : "Yes, mark returned"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmingReturn(false)}
              disabled={returning}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0 text-xs text-muted-foreground"
          onClick={() => setConfirmingReturn(true)}
        >
          {isReturned ? "Undo returned" : "Mark as returned"}
        </Button>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}
